import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/consumer/lib/api'
import { type DisclaimerConfig } from '@/shared/components/claims/disclaimer'

export interface ActionFieldMeta {
  code_name: string
  display_name: string
  field_type: string
  required: boolean
  configuration_data: Record<string, unknown>
}

/**
 * SMS-consent block. Present (non-null) only when the tenant's `sms` capability
 * is enabled - the backend gates it, so the client renders the opt-in checkbox
 * purely off this metadata and never inspects a feature flag itself.
 */
export interface SmsConsentMeta {
  disclosure: string
  opted_in: boolean
}

interface ClaimMetadataResponse {
  fields: ActionFieldMeta[]
  consumer_fields?: ActionFieldMeta[]
  /**
   * Initial values for capture fields, keyed by `code_name`, derived from the
   * holding/product (e.g. `serial_number` carried on the product). Only keys
   * with a value are present.
   */
  prefill?: Record<string, unknown>
  /**
   * Tenant-authored disclaimers keyed by context (e.g. `claim_submit`). Served
   * by the claim-metadata endpoint, resolved via the holding's tenant, so the
   * consumer single-item claim form can render + gate on them.
   */
  disclaimers?: Record<string, DisclaimerConfig>
  sms_consent?: SmsConsentMeta | null
}

export function useConsumerClaimMetadata(holdingUuid: string | undefined) {
  return useQuery<ClaimMetadataResponse>({
    queryKey: ['consumer-claim-metadata', holdingUuid],
    queryFn: async () => {
      const res = await api.get(`/holdings/${holdingUuid}/claim-metadata/`)
      return res.data
    },
    enabled: !!holdingUuid,
    staleTime: 60_000,
  })
}

/**
 * Tenant-authored claim disclaimers for the active consumer tenant, keyed by
 * context. Unlike `useConsumerClaimMetadata`, this is not tied to a holding -
 * it is used by the multi-item claim flow (where several products are claimed
 * together before any single holding is in scope). Returns an empty map for
 * tenants that have authored none, so the disclaimer UI simply renders nothing.
 */
export function useConsumerClaimDisclaimers() {
  return useQuery<{ disclaimers: Record<string, DisclaimerConfig> }>({
    queryKey: ['consumer-claim-disclaimers'],
    queryFn: async () => {
      const res = await api.get('/claims/disclaimers/')
      return res.data?.result ?? res.data
    },
    staleTime: 60_000,
  })
}

interface CreateClaimPayload {
  holding_uuid: string
  captured_values: Record<string, unknown>
  consumer_attribute_values?: Record<string, unknown>
  // Web opt-in / opt-out from the claim form. Only sent when the metadata
  // carried an `sms_consent` block (i.e. the tenant's `sms` flag is on).
  sms_opt_in?: boolean
}

interface CreateClaimResponse {
  uuid: string
  message: string
}

export function useConsumerCreateClaim() {
  return useMutation<CreateClaimResponse, unknown, CreateClaimPayload>({
    mutationFn: async (payload) => {
      const res = await api.post('/claims/', payload)
      return res.data
    },
  })
}

// ── Multi-item (register + claim in one step) ────────────

export interface ConsumerRegisterAndClaimPayload {
  claim_schema_key: string
  lines: {
    product_uuid: string
    quantity?: number
    registration_field_values?: Record<string, unknown>
    attribute_values?: Record<string, unknown>
  }[]
  shared_attribute_values?: Record<string, unknown>
}

export interface RegisterAndClaimResult {
  created: number
  results: Array<{
    row: number
    holding_uuid: string
    claim_uuid: string
    claim_number: string
  }>
  parent_uuid?: string
  parent_claim_number?: string
}

/**
 * Register several items + file a claim for each in one request, under one
 * umbrella parent - the consumer multi-item flow. The consumer identity is
 * session-derived server-side, so it is never part of the payload. The
 * response envelope nests under ``result``.
 */
export function useConsumerRegisterAndClaim() {
  return useMutation<
    RegisterAndClaimResult,
    unknown,
    ConsumerRegisterAndClaimPayload
  >({
    mutationFn: async (payload) => {
      const res = await api.post('/claims/register-and-claim/', payload)
      return res.data?.result ?? res.data
    },
  })
}
